import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { BeatLoader } from 'react-spinners';
import {
  FiRefreshCw,
  FiServer,
  FiSettings,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { getMqttInstance } from '../../../services/mqtt/mqttService';
import AgentList from './AgentList';
import AgentDetailPanel from './AgentDetailPanel';
import DirectAgentView from './DirectAgentView';

// Custom tab component implementation

const AgentDashboard = () => {
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mqttStatus, setMqttStatus] = useState('initializing');
  const [connectionError, setConnectionError] = useState(null);
  const [activeTab, setActiveTab] = useState('agents');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track MQTT ready state
  const [isMqttReady, setIsMqttReady] = useState(false);

  // Initialize MQTT service
  useEffect(() => {
    let isMounted = true;
    let mqttClient = null;

    // Define handlers in the effect's scope so cleanup can access them
    const handleConnect = () => {
      console.log('MQTT connected event received');
      if (!isMounted) return;
      setMqttStatus('connected');
      setConnectionError(null);
      toast.success('Connected to MQTT broker');
    };

    const handleDisconnect = () => {
      console.log('MQTT disconnect event received');
      if (!isMounted) return;
      setMqttStatus('disconnected');
      toast.warning('Disconnected from MQTT broker - attempting to reconnect...');
    };

    const handleError = err => {
      console.error('MQTT error event:', err);
      if (!isMounted) return;
      setMqttStatus('failed');
      setConnectionError(err?.message || 'Unknown connection error');
      toast.error(`MQTT error: ${err?.message || 'Unknown error'}`);
    };

    const handleOffline = () => {
      console.log('MQTT offline event received');
      if (!isMounted) return;
      setMqttStatus('suspended');
      setConnectionError('Connection suspended - check network connection');
      toast.warning('MQTT connection suspended - check network connection');
    };

    const initMqttConnection = async () => {
      try {
        console.log('Initializing MQTT connection...');
        setMqttStatus('initializing');
        setIsLoading(true);
        setConnectionError(null);

        mqttClient = await getMqttInstance();
        if (!isMounted) return;

        console.log('MQTT client initialized successfully');
        setIsMqttReady(true);

        if (mqttClient.connected) {
          console.log('MQTT client is already connected upon initialization.');
          handleConnect(); // Use the handler to ensure consistent state updates
        }

        // Add event listeners
        mqttClient.on('connect', handleConnect);
        mqttClient.on('disconnect', handleDisconnect);
        mqttClient.on('error', handleError);
        mqttClient.on('offline', handleOffline);
      } catch (error) {
        console.error('Error initializing MQTT:', error);
        if (isMounted) {
          handleError(error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initMqttConnection();

    // This is the correct cleanup function for useEffect
    return () => {
      isMounted = false;
      console.log('AgentDashboard unmounting, cleaning up MQTT listeners.');
      if (mqttClient) {
        console.log('Removing MQTT event listeners.');
        mqttClient.off('connect', handleConnect);
        mqttClient.off('disconnect', handleDisconnect);
        mqttClient.off('error', handleError);
        mqttClient.off('offline', handleOffline);
      }
    };
  }, []);

  // Handle tab change
  const handleTabChange = key => {
    setActiveTab(key);
  };

  // Handle selecting an agent for detailed view
  const handleSelectAgent = agentId => {
    setSelectedAgentId(agentId);
  };

  // Close detail panel
  const handleCloseDetailPanel = () => {
    setSelectedAgentId(null);
  };

  // Handle refresh
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    // Any additional refresh logic can be added here
    setTimeout(() => setIsRefreshing(false), 1000);
  }, []);

  // Update last updated timestamp
  const updateLastUpdated = useCallback(() => {
    setLastUpdated(new Date().toLocaleTimeString());
  }, []);

  // Initial data load
  useEffect(() => {
    updateLastUpdated();
  }, [updateLastUpdated]);

  // Render connection status badge
  const renderConnectionStatus = () => {
    let statusText = '';
    let bgColor = '';
    let statusIcon = null;

    switch (mqttStatus) {
      case 'connected':
        statusText = 'Connected';
        bgColor = 'bg-green-100 text-green-800';
        statusIcon = <FiCheckCircle className="text-green-500" />;
        break;
      case 'disconnected':
        statusText = 'Disconnected';
        bgColor = 'bg-amber-100 text-amber-800';
        statusIcon = <FiAlertCircle className="text-amber-500" />;
        break;
      case 'failed':
        statusText = 'Connection Failed';
        bgColor = 'bg-red-100 text-red-800';
        statusIcon = <FiXCircle className="text-red-500" />;
        break;
      case 'suspended':
        statusText = 'Suspended';
        bgColor = 'bg-amber-100 text-amber-800';
        statusIcon = <FiAlertCircle className="text-amber-500" />;
        break;
      default:
        statusText = 'Initializing...';
        bgColor = 'bg-blue-100 text-blue-800';
        statusIcon = <BeatLoader color="#3B82F6" size={8} margin={2} />;
    }

    return (
      <div className="flex items-center space-x-2">
        <span className="text-gray-500 text-sm">MQTT Status:</span>
        <div className={`px-2 py-1 rounded-full flex items-center gap-1 ${bgColor}`}>
          {statusIcon}
          <span className="text-sm">{statusText}</span>
        </div>
        {lastUpdated && <span className="text-gray-500 text-xs">Last updated: {lastUpdated}</span>}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center justify-center">
          <BeatLoader color="#3B82F6" size={12} margin={5} />
          <p className="mt-3 text-gray-600">Initializing agent dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Agent Management</h2>
          <div className="flex items-center space-x-4">
            <button
              className="px-3 py-1 bg-blue-500 text-white rounded flex items-center gap-2 hover:bg-blue-600 disabled:bg-blue-300"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? <FiRefreshCw className="animate-spin" /> : <FiRefreshCw />}
              Refresh
            </button>
            {renderConnectionStatus()}
          </div>
        </div>
        <div className="border-b px-4 flex">
          <button
            className={`py-3 px-4 border-b-2 flex items-center gap-2 ${activeTab === 'agents' ? 'border-blue-500 text-blue-500' : 'border-transparent'}`}
            onClick={() => handleTabChange('agents')}
          >
            <FiServer />
            <span>Agents</span>
          </button>
          <button
            className={`py-3 px-4 border-b-2 flex items-center gap-2 ${activeTab === 'direct' ? 'border-blue-500 text-blue-500' : 'border-transparent'}`}
            onClick={() => handleTabChange('direct')}
          >
            <FiSettings />
            <span>Direct Control</span>
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'agents' && (
            <AgentList onAgentSelect={handleSelectAgent} onRefresh={updateLastUpdated} />
          )}

          {activeTab === 'direct' && (
            <DirectAgentView
              mqttReady={isMqttReady}
              mqttStatus={mqttStatus}
              connectionError={connectionError}
            />
          )}
        </div>
      </div>

      {selectedAgentId && (
        <AgentDetailPanel agentId={selectedAgentId} onClose={handleCloseDetailPanel} />
      )}
    </div>
  );
};

export default AgentDashboard;
